import moment from 'moment';
import { countries } from 'country-data';

import { RolesNames } from '../../enums';
import {
  FillBgBriefcaseIcon,
  FillBgStarIcon,
  FillBgUserIcon
} from '../../components/sicons';

export { getInitials } from './getInitials';

export const calculatePrecentage = (
  base: number,
  of: number,
  ignoreZero?: boolean
) => {
  const calculation = Number(((base / of) * 100).toFixed(1));

  if (!calculation || calculation === Infinity || calculation === -Infinity)
    return 0;

  if (!ignoreZero && calculation < 10) return `0${calculation}`;

  return calculation;
};

export const getPercentage = (base: number, of: number) =>
  Math.floor((base / of) * 100) || 0;

export const daysRemaining = (date: string) => {
  const eventdate = moment(date);
  const todaysdate = moment();

  return eventdate.diff(todaysdate, 'days');
};

/**
 * Counts the number of days that have passed from the indicated date until today.
 * Returns 0 if the date is in the future.
 *
 * @param fromDate date with which we count
 * @param toDate the date to which the counting is carried out (current date by default)
 * @returns The number of days between FromDate and today (min: 0)
 */
export const daysSince = (fromDate: string, toDate?: string): number => {
  const then = moment(fromDate);
  const now = moment(toDate);

  const diff = now.diff(then, 'days');
  return Math.max(diff, 0);
};

export const cleanSearchString = (str: string) => {
  if (!str) return '';

  const tempstring = str.replace(/\+/g, ' ');

  return tempstring;
};

export const generateRole = (role: number) => {
  if (!role) return '-';

  return Object.values(RolesNames)?.[role - 1] || '-';
};

export const generateRoleIcon = (role: number) => {
  if (!role) return '-';

  const icons = [
    <FillBgBriefcaseIcon />,
    <FillBgUserIcon />,
    <FillBgStarIcon htmlColor="#572BD3" />
  ];

  return icons?.[role - 1] || '-';
};

export const generateCountryCode = (fullName: string) => {
  if (!fullName) return '-';
  const countryCode = countries.all.find(code => code.name === fullName);

  if (countryCode) return countryCode.alpha2;

  return '-';
};

export const downloadResource = async (url: string) =>
  window.open(url, '_blank');

export const printResource = async (url: string, fallback?: string) => {
  try {
    const res = await fetch(url, { method: 'GET' });

    if (!res.ok) {
      window?.open(fallback, '_blank');
      return;
    }

    const blob = await res.blob();
    const fileURL = URL.createObjectURL(blob);
    const newWindow = window.open(fileURL, '_blank');

    if (newWindow) {
      newWindow.onload = () => {
        newWindow.focus();
        newWindow.print();
      };
    }
  } catch (error) {
    // fallback open if fetch fails
    window?.open(url, '_blank');
  }
};

export const printMergedResources = async (
  urls: string[],
  fallback?: string
) => {
  try {
    // Step 1: Fetch all files
    const blobs = await Promise.all(
      urls.map(async url => {
        const res = await fetch(url);
        if (!res.ok) throw new Error(`Failed to fetch ${url}`);
        return res.blob();
      })
    );

    // Step 2: Create image URLs from blobs
    const imageUrls = blobs.map(blob => URL.createObjectURL(blob));

    // Step 3: Build a printable HTML page with all images
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      return;
    }

    const html = `
      <html>
        <head>
          <title>Print Files</title>
          <style>
            body { margin: 0; padding: 0; }
            img { display: block; width: 100%; page-break-after: always; }
          </style>
        </head>
        <body>
          ${imageUrls.map(url => `<img src="${url}" />`).join('')}
          <script>
            window.onload = () => {
              window.focus();
              window.print();
            };
          </script>
        </body>
      </html>
    `;

    // Step 4: Write and print
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
  } catch (err) {
    if (fallback) window.open(fallback, '_blank');
  }
};

export const getDaysLeft = (dateStr: string): number => {
  const delivery = new Date(dateStr);
  const today = new Date();

  today.setHours(0, 0, 0, 0);
  delivery.setHours(0, 0, 0, 0);

  const diff = delivery.getTime() - today.getTime();
  return Math.ceil(diff / (1000 * 60 * 60 * 24)) + 1;
};
